import { useEffect, useMemo, useState } from "react";
import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Activity, BadgeCheck, BarChart3, Bell, Bot, CalendarClock, ClipboardCheck, Coins, DownloadCloud,
  FileBarChart,
  FileClock, Filter,
  LayoutDashboard, ListChecks, LogOut, Megaphone, Menu, PhoneCall, PhoneForwarded, Plug, RefreshCw,
  Rocket, Search, Send, Settings2, ShieldBan, Target, UploadCloud, Users, X,
} from "lucide-react";
import { toast } from "sonner";
import { api, apiError, get } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ConfirmDialog } from "@/components/common/ConfirmDialog";
import { relative, title } from "@/lib/format";
import { cn } from "@/lib/utils";

const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, resource: "dashboard", color: "text-indigo-400" },
  { to: "/campaigns", label: "Campaigns", icon: Megaphone, resource: "campaigns", color: "text-violet-400" },
  { to: "/agents", label: "AI Agents", icon: Bot, resource: "agents", color: "text-fuchsia-400" },
  { to: "/contacts", label: "Contacts", icon: Users, resource: "contacts", color: "text-sky-400" },
  { to: "/lists", label: "Contact Lists", icon: ListChecks, resource: "lists", color: "text-cyan-400" },
  { to: "/lists/segments", label: "Lists & Segments", icon: Filter, resource: "lists", color: "text-teal-400" },
  { to: "/lists/import-jobs", label: "Import Jobs", icon: UploadCloud, resource: "lists", color: "text-emerald-400" },
  { to: "/lists/export-jobs", label: "Export Jobs", icon: DownloadCloud, resource: "exports", color: "text-lime-400" },
  { to: "/calls", label: "Calls", icon: PhoneCall, resource: "calls", color: "text-amber-400" },
  { to: "/live-calls", label: "Live Calls", icon: Activity, resource: "live_calls", color: "text-rose-400" },
  { to: "/leads", label: "Leads", icon: Target, resource: "leads", color: "text-orange-400" },
  { to: "/callbacks", label: "Callbacks", icon: CalendarClock, resource: "callbacks", color: "text-yellow-400" },
  { to: "/phone-numbers", label: "Phone Numbers", icon: PhoneForwarded, resource: "phone_numbers", color: "text-blue-400" },
  { to: "/do-not-call", label: "Do Not Call", icon: ShieldBan, resource: "dnc", color: "text-red-400" },
  { to: "/analytics", label: "Analytics", icon: BarChart3, resource: "analytics", color: "text-purple-400" },
  { to: "/reports", label: "Daily Report", icon: FileBarChart, resource: "analytics", color: "text-purple-300" },
  { to: "/costs", label: "Costs & Usage", icon: Coins, resource: "costs", color: "text-pink-400" },
  { to: "/quality", label: "Quality Control", icon: ClipboardCheck, resource: "quality", color: "text-green-400" },
  { to: "/users", label: "Users & Roles", icon: BadgeCheck, resource: "users", color: "text-sky-300" },
  { to: "/setup", label: "Setup Assistant", icon: Rocket, resource: "settings", color: "text-emerald-300" },
  { to: "/provider-center", label: "Provider Center", icon: Plug, resource: "providers", color: "text-indigo-300" },
  { to: "/crm", label: "CRM", icon: Send, resource: "settings", color: "text-orange-300" },
  { to: "/reconciliation", label: "Reconciliation", icon: RefreshCw, resource: "settings", color: "text-teal-300" },
  { to: "/settings", label: "Settings", icon: Settings2, resource: "settings", color: "text-slate-300" },
  { to: "/audit-log", label: "Audit Log", icon: FileClock, resource: "audit", color: "text-violet-300" },
];

export function BrandMark({ compact = false }) {
  return (
    <span className="flex items-center gap-2.5">
      <span className="flex h-8 w-8 items-center justify-center rounded-lg border border-white/25 bg-white/5">
        <PhoneCall className="h-4 w-4 text-white" strokeWidth={2.2} />
      </span>
      {compact ? null : (
        <span className="font-display text-xl font-bold tracking-tight text-white">ai2Dial</span>
      )}
    </span>
  );
}

function GlobalSearch() {
  const [term, setTerm] = useState("");
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { data } = useQuery({
    queryKey: ["search", term],
    queryFn: () => get("/search", { q: term }),
    enabled: term.length >= 2,
  });

  const groups = useMemo(
    () => [
      { key: "campaigns", label: "Campaigns", path: "/campaigns", render: (i) => i.name },
      { key: "contacts", label: "Contacts", path: "/contacts", render: (i) => `${i.first_name} ${i.last_name} · ${i.phone}` },
      { key: "calls", label: "Calls", path: "/calls", render: (i) => `${i.contact_name} · ${title(i.outcome)}` },
      { key: "leads", label: "Leads", path: "/leads", render: (i) => `${i.contact_name} · ${title(i.status)}` },
    ],
    []
  );

  return (
    <div className="relative w-full max-w-md">
      <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
      <Input
        data-testid="global-search-input"
        value={term}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 180)}
        onChange={(e) => setTerm(e.target.value)}
        placeholder="Search campaigns, contacts, calls, leads…"
        className="h-9 border-slate-200 bg-slate-50 pl-9 text-sm focus:bg-white"
      />
      {open && term.length >= 2 && data ? (
        <div
          data-testid="global-search-results"
          className="absolute left-0 right-0 top-11 z-50 max-h-96 overflow-y-auto rounded-lg border border-slate-200 bg-white shadow-md"
        >
          {groups.every((g) => (data[g.key] || []).length === 0) ? (
            <p className="px-3 py-3 text-sm text-slate-500">No matches found.</p>
          ) : (
            groups.map((group) =>
              (data[group.key] || []).length ? (
                <div key={group.key} className="border-b border-slate-100 py-1.5 last:border-0">
                  <p className="px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                    {group.label}
                  </p>
                  {data[group.key].map((item) => (
                    <button
                      key={item.id}
                      data-testid={`search-result-${item.id}`}
                      className="block w-full px-3 py-1.5 text-left text-sm text-slate-700 transition-colors duration-150 hover:bg-slate-50"
                      onClick={() => {
                        navigate(`${group.path}/${item.id}`);
                        setTerm("");
                      }}
                    >
                      {group.render(item)}
                    </button>
                  ))}
                </div>
              ) : null
            )
          )}
        </div>
      ) : null}
    </div>
  );
}

function Notifications() {
  const [open, setOpen] = useState(false);
  const queryClient = useQueryClient();
  const { data } = useQuery({
    queryKey: ["notifications"],
    queryFn: () => get("/notifications"),
    refetchInterval: 20000,
  });
  const markRead = useMutation({
    mutationFn: () => api.post("/notifications/read"),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["notifications"] }),
  });

  return (
    <div className="relative">
      <Button
        data-testid="notifications-button"
        variant="ghost"
        size="icon"
        className="relative"
        onClick={() => setOpen((v) => !v)}
      >
        <Bell className="h-4.5 w-4.5 text-slate-600" />
        {data?.unread ? (
          <span className="absolute right-1.5 top-1.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-rose-600 px-1 text-[10px] font-semibold text-white">
            {data.unread}
          </span>
        ) : null}
      </Button>
      {open ? (
        <div
          data-testid="notifications-panel"
          className="absolute right-0 top-11 z-50 w-80 rounded-lg border border-slate-200 bg-white shadow-md"
        >
          <div className="flex items-center justify-between border-b border-slate-200 px-3 py-2">
            <span className="text-sm font-semibold text-slate-800">Notifications</span>
            <button
              data-testid="notifications-mark-read"
              className="text-xs font-medium text-blue-600 hover:underline"
              onClick={() => markRead.mutate()}
            >
              Mark all read
            </button>
          </div>
          <div className="max-h-80 overflow-y-auto">
            {(data?.items || []).length === 0 ? (
              <p className="px-3 py-4 text-sm text-slate-500">No notifications.</p>
            ) : (
              data.items.map((n) => (
                <div key={n.id} className="border-b border-slate-100 px-3 py-2 last:border-0">
                  <div className="flex items-start gap-2">
                    <span
                      className={cn(
                        "mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full",
                        n.level === "destructive" ? "bg-rose-500" : n.level === "warning" ? "bg-amber-500" : n.level === "success" ? "bg-emerald-500" : "bg-blue-500"
                      )}
                    />
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-slate-800">{n.title}</p>
                      <p className="text-xs text-slate-500">{n.message}</p>
                      <p className="mt-0.5 text-[11px] text-slate-400">{relative(n.created_at)}</p>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default function AppShell() {
  const { user, logout, can } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [stopOpen, setStopOpen] = useState(false);
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  useEffect(() => setMobileOpen(false), [navigate]);

  const items = NAV.filter((item) => can(item.resource, "read"));

  const emergencyStop = async (reason) => {
    try {
      const { data } = await api.post("/emergency-stop", { reason, confirm: true });
      toast.success(`Emergency stop executed: ${data.campaigns_paused} campaigns paused, ${data.calls_stopped} calls stopped`);
      queryClient.invalidateQueries();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const sidebar = (
    <nav className="flex h-full flex-col gap-1 overflow-y-auto px-3 py-4">
      <Link to="/dashboard" data-testid="brand-link" className="mb-4 flex items-center gap-2 px-1.5">
        <BrandMark />
      </Link>
      {items.map((item) => (
        <NavLink
          key={item.to}
          to={item.to}
          data-testid={`nav-${item.to.replace("/", "")}`}
          className={({ isActive }) => cn("nav-item", isActive && "nav-item-active")}
        >
          {({ isActive }) => (
            <>
              <item.icon className={cn("h-4 w-4 shrink-0 transition-colors", isActive ? "text-white" : item.color)} />
              {item.label}
            </>
          )}
        </NavLink>
      ))}
      <div className="mt-auto px-2 pt-4 text-[11px] font-medium text-slate-500">
        ai2Dial · v1.0
      </div>
    </nav>
  );

  return (
    <div className="flex h-screen w-full overflow-hidden bg-slate-50">
      <aside className="hidden w-60 shrink-0 border-r border-slate-800 bg-slate-950 lg:block">
        {sidebar}
      </aside>
      {mobileOpen ? (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-slate-900/60" onClick={() => setMobileOpen(false)} />
          <div className="absolute left-0 top-0 h-full w-64 border-r border-slate-800 bg-slate-950">
            <button
              data-testid="mobile-nav-close"
              className="absolute right-2 top-3 rounded-md p-1.5 text-slate-400 hover:bg-white/10 hover:text-white"
              onClick={() => setMobileOpen(false)}
            >
              <X className="h-4 w-4" />
            </button>
            {sidebar}
          </div>
        </div>
      ) : null}

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-40 flex h-16 shrink-0 items-center gap-3 border-b border-slate-200/80 bg-white/90 px-4 backdrop-blur-md md:px-6">
          <Button
            data-testid="mobile-nav-open"
            variant="ghost"
            size="icon"
            className="lg:hidden"
            onClick={() => setMobileOpen(true)}
          >
            <Menu className="h-5 w-5" />
          </Button>
          <span className="lg:hidden">
            <BrandMark compact />
          </span>
          <GlobalSearch />
          <div className="ml-auto flex items-center gap-2">
            {can("live_calls", "write") ? (
              <Button
                data-testid="emergency-stop-button"
                onClick={() => setStopOpen(true)}
                className="h-9 bg-rose-600 font-semibold text-white shadow-md shadow-rose-600/30 transition-all duration-200 hover:bg-rose-700 active:scale-95"
              >
                Emergency stop
              </Button>
            ) : null}
            <Notifications />
            <div className="hidden items-center gap-2 border-l border-slate-200 pl-3 md:flex">
              <img
                alt={user?.name}
                src={`https://ui-avatars.com/api/?name=${encodeURIComponent(user?.name || "User")}&background=4F46E5&color=fff`}
                className="h-8 w-8 rounded-full ring-2 ring-indigo-100"
              />
              <div className="leading-tight">
                <p data-testid="current-user-name" className="text-sm font-medium text-slate-800">
                  {user?.name}
                </p>
                <p className="text-[11px] text-slate-500">{user?.role_label}</p>
              </div>
            </div>
            <Button data-testid="logout-button" variant="ghost" size="icon" onClick={logout}>
              <LogOut className="h-4 w-4 text-slate-500" />
            </Button>
          </div>
        </header>
        <main className="flex-1 overflow-y-auto p-4 md:p-6">
          <div className="mx-auto max-w-[1600px] space-y-6">
            <Outlet />
          </div>
        </main>
      </div>

      <ConfirmDialog
        open={stopOpen}
        onOpenChange={setStopOpen}
        destructive
        requireReason
        reasonLabel="Reason for emergency stop"
        title="Stop all calling activity?"
        description="This cancels queued calls, prevents new calls from starting, ends active calls and pauses every running campaign. The action is recorded in the audit log."
        confirmLabel="Execute emergency stop"
        onConfirm={emergencyStop}
        testId="emergency-stop-dialog"
      />
    </div>
  );
}